import React from 'react';
import { type MatchComposition, type MatchDetail, MatchStatus, displayRoundDetail } from '@iziping/common';
import { Ban, Play, Trophy } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import styles from './PlayerMatchHistory.module.css';

interface PlayerMatchHistoryProps {
    matches: MatchDetail[];
    playerId: number;
}

function isQualified(opponents: MatchComposition[], playerId: number): boolean | undefined {
    return opponents.find((opp) => opp.heatRegistration.players.some((p) => p.id === playerId))?.qualified;
}

interface OpponentInfo {
    name: string;
    points?: number;
}

function getOpponents(opponents: MatchComposition[], playerId: number): OpponentInfo[] {
    return opponents
        .filter((opp) => !opp.heatRegistration.players.some((p) => p.id === playerId))
        .flatMap((opp) => opp.heatRegistration.players)
        .map((p) => ({ name: `${p.firstname} ${p.lastname.toUpperCase()}`, points: p.points }));
}

function matchDuration(match: MatchDetail): string | null {
    if (match.status !== MatchStatus.FINISHED || !match.begin || !match.end) return null;
    const minutes = Math.round((new Date(match.end).getTime() - new Date(match.begin).getTime()) / 60000);
    return `${minutes} min`;
}

const PlayerMatchHistory: React.FC<PlayerMatchHistoryProps> = ({ matches, playerId }) => {
    const { t } = useTranslation();

    if (!matches.length) {
        return <p className={styles.emptyState}>{t('match.result.empty')}</p>;
    }

    return (
        <div className={styles.matchList}>
            {matches.map((match) => {
                const isFinal = match.type === 'TABLEAU_FINAL' && match.round === 0;
                const qualified =
                    match.status === MatchStatus.FINISHED ? isQualified(match.opponents, playerId) : undefined;
                const duration = matchDuration(match);
                const opponents = getOpponents(match.opponents, playerId);

                return (
                    <div key={match.id} className={`${styles.matchRow} ${isFinal ? styles.matchRowFinal : ''}`}>
                        <div className={styles.matchLeft}>
                            <span className={styles.matchHeat}>{match.heatName}</span>
                            <span className={`${styles.matchRound} ${isFinal ? styles.matchRoundFinal : ''}`}>
                                {displayRoundDetail(match)}
                            </span>
                        </div>

                        <div className={styles.matchOpponents}>
                            <span className={styles.vsLabel}>vs</span>
                            {opponents.length ? (
                                <span className={styles.opponentNames}>
                                    {opponents.map((opp, i) => (
                                        <React.Fragment key={i}>
                                            {i > 0 && ' · '}
                                            {opp.name}
                                            {opp.points != null && (
                                                <span className={styles.opponentPoints}>{opp.points} pts</span>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </span>
                            ) : (
                                <span className={styles.opponentNames}>—</span>
                            )}
                        </div>

                        {match.status === MatchStatus.FINISHED ? (
                            qualified ? (
                                <span className={`${styles.resultBadge} ${isFinal ? styles.winner : styles.qualified}`}>
                                    <Trophy size={11} aria-hidden="true" />
                                    {isFinal ? t('match.result.winner') : t('match.result.qualified')}
                                </span>
                            ) : (
                                <span className={`${styles.resultBadge} ${styles.eliminated}`}>
                                    <Ban size={11} aria-hidden="true" /> {t('match.result.eliminated')}
                                </span>
                            )
                        ) : match.status === MatchStatus.RUNNING ? (
                            <span className={`${styles.resultBadge} ${styles.running}`}>
                                <Play size={11} aria-hidden="true" /> {t('match.result.running')}
                            </span>
                        ) : (
                            <span className={`${styles.resultBadge} ${styles.pending}`}>
                                {t('match.result.pending')}
                            </span>
                        )}

                        <div className={styles.matchMeta}>
                            {match.table && <span>Table {match.table.number}</span>}
                            {duration && <span className={styles.matchDuration}>{duration}</span>}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default PlayerMatchHistory;
